import type { AuthResponse, Locale, User } from '@app/shared'

import { api } from '../lib/http'
import { clearTokens, getRefreshToken, setTokens } from '../lib/session'

/** Every call here is anonymous: these endpoints create the session. */
const anon = { anonymous: true } as const

/**
 * `language` is the interface language the person is signing up in. It becomes
 * the profile language, so the verification letter — and every e-mail after it
 * — arrives in the language they are reading the screen in.
 */
export async function register(
  email: string,
  password: string,
  referralCode?: string,
  language?: Locale,
): Promise<AuthResponse> {
  const result = await api.post<AuthResponse>(
    '/auth/register',
    {
      email,
      password,
      ...(referralCode ? { referralCode } : {}),
      ...(language ? { language } : {}),
    },
    anon,
  )
  setTokens(result.tokens)
  return result
}

export async function login(email: string, password: string, totpCode?: string): Promise<AuthResponse> {
  const result = await api.post<AuthResponse>(
    '/auth/login',
    { email, password, ...(totpCode ? { totpCode } : {}) },
    anon,
  )
  setTokens(result.tokens)
  return result
}

/**
 * Ends this device's session — the family the stored refresh token belongs to
 * — and nothing else: other devices stay signed in, which is what "Sessions
 * and devices" is for. Without a token the server ends every session, which
 * is the right reading of a sign-out that has lost track of its own.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken()
  try {
    await api.post<void>('/auth/logout', refreshToken === null ? {} : { refreshToken })
  } finally {
    // The local session goes regardless: a failed revoke must not strand the
    // user in a half-signed-in state.
    clearTokens()
  }
}

/**
 * Answers with the verified user, not with a session: a link opened in a
 * browser that has never signed in must not be turned into one.
 */
export function verifyEmail(token: string): Promise<User> {
  return api.post<User>('/auth/verify-email', { token }, anon)
}

export function resendVerification(email: string): Promise<void> {
  return api.post<void>('/auth/resend-verification', { email }, anon)
}

export function forgotPassword(email: string): Promise<void> {
  return api.post<void>('/auth/forgot-password', { email }, anon)
}

export function resetPassword(token: string, password: string): Promise<void> {
  return api.post<void>('/auth/reset-password', { token, password }, anon)
}
